import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { changePasswordRequest } from './api';

/**
 * Modal de auto-serviço pra o usuário trocar a própria senha.
 * Exige a senha atual + nova (mín. 8) + confirmação (F1.4). Ao trocar, DERRUBA
 * todas as sessões (inclusive esta) e manda relogar — os acessos anteriores
 * caem em ≤60s no servidor.
 */
export function ChangePasswordModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEscapeClose(open, onClose);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => changePasswordRequest({ current_password: current, new_password: next }),
    onSuccess: () => {
      toast.success('Senha alterada', {
        description: 'Entre novamente com a nova senha.',
      });
      // Trocar a senha derruba TODAS as sessões (inclusive esta) — relogar.
      window.setTimeout(() => {
        window.location.href = '/login';
      }, 1200);
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Falha ao alterar a senha',
      ),
  });

  // Limpa os campos ao FECHAR — o modal fica montado o tempo todo (controlado
  // por `open`), então sem isso o que foi digitado persiste ao reabrir. Reset
  // no fechar (não no abrir) evita flash do valor antigo na reabertura.
  useEffect(() => {
    if (!open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !mut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !mut.isPending) onClose();
      }}
    >
      <div className="w-[420px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-bold text-[16px] text-n-900">
            <KeyRound size={18} aria-hidden="true" /> Alterar senha
          </h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <PasswordField label="Senha atual" value={current} onChange={setCurrent} />
          <PasswordField
            label="Nova senha"
            value={next}
            onChange={setNext}
            hint="Mínimo 8 caracteres."
          />
          <PasswordField label="Confirmar nova senha" value={confirm} onChange={setConfirm} />
          {mismatch && (
            <span className="text-[11px] text-danger-600">As senhas não coincidem.</span>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={!canSubmit}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Alterar senha
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Campo de senha com botão "olhinho" (mostrar/ocultar) + dica opcional. */
function PasswordField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <label className="flex flex-col gap-1">
      <span className="font-semibold text-[12px] text-n-700">{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 pr-10 text-[13px] outline-none focus:border-brand-500"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          title={show ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-n-500 transition-colors hover:text-n-700"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {hint && <span className="text-[11px] text-n-500">{hint}</span>}
    </label>
  );
}
